import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PersonalAvatarViewData, PersonalAvatarStatus, getPersonalAvatarsByIds } from '@/api/personal-avatars';
import { useCurrentApp } from '@/stores/userDataStore';

type PersonalAvatarPollingContextData = {
  pendingAvatars: PersonalAvatarViewData[];
  failedAvatars: string[];
  completedAvatars: PersonalAvatarViewData[];
  addAvatarToPollingQueue: (avatar: PersonalAvatarViewData) => void;
  setPollingQueue: (avatars: PersonalAvatarViewData[]) => void;
};

const PersonalAvatarPollingContext = createContext<PersonalAvatarPollingContextData | null>(null);

const POLLING_INTERVAL = 5_000;

type PersonalAvatarPollingProviderProps = {
  children: React.ReactNode;
  onCompletion?: (avatars: PersonalAvatarViewData[]) => void;
};

export function PersonalAvatarPollingProvider({ children, onCompletion }: PersonalAvatarPollingProviderProps) {
  const { appKey, isLoggedIn } = useCurrentApp();
  const [completedAvatars, setCompletedAvatars] = useState<PersonalAvatarViewData[]>([]);
  const [failedAvatars, setFailedAvatars] = useState<string[]>([]);
  const [pendingAvatars, setPendingAvatars] = useState<PersonalAvatarViewData[]>([]);

  const addAvatar = useCallback(
    (avatars: PersonalAvatarViewData[]) => {
      setCompletedAvatars((prev) => [...prev, ...avatars]);
      if (onCompletion) onCompletion(avatars);
    },
    [setCompletedAvatars, onCompletion]
  );

  const setPollingQueue = useCallback(
    (avatars: PersonalAvatarViewData[]) => {
      setPendingAvatars(avatars);
    },
    [setPendingAvatars]
  );

  const addAvatarToPollingQueue = useCallback(
    (avatar: PersonalAvatarViewData) => {
      setPendingAvatars((prev) => [...prev, avatar]);
    },
    [setPendingAvatars]
  );

  const pollingQuery = useQuery({
    queryKey: ['personal-avatar-polling', ...pendingAvatars.map((avatar) => avatar.id)],
    queryFn: async () =>
      getPersonalAvatarsByIds({
        appKey: appKey,
        ids: pendingAvatars.map((avatar) => avatar.id),
      }),
    enabled: pendingAvatars.length > 0 && isLoggedIn,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
    refetchInterval: ({ state }) => {
      const { data } = state;
      if (!data) return POLLING_INTERVAL;
      if (data.some((avatar) => avatar.status === PersonalAvatarStatus.PROCESSING)) {
        return POLLING_INTERVAL;
      }
      return false;
    },
  });

  useEffect(() => {
    if (pollingQuery.data) {
      const completedAvatars: PersonalAvatarViewData[] = [];
      const failedAvatars: string[] = [];
      const newPendingAvatars = pendingAvatars.filter((pendingItem) => {
        const updatedItem = pollingQuery.data.find((avatar) => avatar.id === pendingItem.id);

        if (!updatedItem) return false;
        if (updatedItem?.status === PersonalAvatarStatus.DONE) {
          completedAvatars.push(updatedItem);
          return false;
        }

        if (updatedItem?.status === PersonalAvatarStatus.FAILED) {
          failedAvatars.push(updatedItem.id);
          return false;
        }

        return true;
      });
      setPendingAvatars(newPendingAvatars);
      if (completedAvatars.length > 0) {
        addAvatar(completedAvatars);
      }
      if (failedAvatars.length > 0) {
        setFailedAvatars((prev) => [...prev, ...failedAvatars]);
      }
    }
  }, [pollingQuery.data]);

  const contextValue = useMemo(
    () => ({
      pendingAvatars,
      failedAvatars,
      addAvatarToPollingQueue,
      completedAvatars,
      setPollingQueue,
    }),
    [pendingAvatars, failedAvatars, addAvatarToPollingQueue, completedAvatars, setPollingQueue]
  );

  return <PersonalAvatarPollingContext.Provider value={contextValue}>{children}</PersonalAvatarPollingContext.Provider>;
}

export const usePersonalAvatarPollingContext = () => {
  const context = useContext(PersonalAvatarPollingContext);
  if (context === null) {
    throw new Error('usePersonalAvatarPollingContext must be used within an PersonalAvatarPollingProvider');
  }
  return context;
};

export const useCompletedPersonalAvatar = (avatarId?: string) => {
  const { pendingAvatars, failedAvatars, completedAvatars } = usePersonalAvatarPollingContext();

  return useMemo(
    () => ({
      isLoading: !!avatarId && pendingAvatars.some((avatar) => avatar.id === avatarId),
      avatar: completedAvatars.find((avatar) => avatar.id === avatarId) || null,
      hasError: !!avatarId && failedAvatars.includes(avatarId),
    }),
    [avatarId, pendingAvatars, completedAvatars, failedAvatars]
  );
};

export const useCompletedPersonalAvatars = (avatarIds: string[]) => {
  const { pendingAvatars, failedAvatars, completedAvatars } = usePersonalAvatarPollingContext();

  return useMemo(
    () => ({
      isLoading: !!avatarIds.length && pendingAvatars.some((avatar) => avatarIds.includes(avatar.id)),
      avatars: completedAvatars.filter((avatar) => avatarIds.includes(avatar.id)),
      hasError: !!avatarIds.length && failedAvatars.some((id) => avatarIds.includes(id)),
    }),
    [avatarIds, pendingAvatars, completedAvatars, failedAvatars]
  );
};
